import { useQuery } from "@tanstack/react-query";
import { ChevronDown, Database, GitBranch, Loader2, Search, Upload } from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
	ConfirmDialog,
	Dialog,
	DialogBody,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { badgeBase, statusClasses } from "@/lib/colors";
import { cn } from "@/lib/utils";
import {
	formatDateTime,
	helpdeskApi,
	type Run,
	type RunKind,
	useHelpdeskKey,
	useHelpdeskMutation,
} from "./api";

const KIND_LABEL: Record<RunKind, string> = {
	otrs_dry_run: "Import z OTRS — próba",
	otrs_import: "Import z OTRS",
	hindsight_seed: "Zasilenie Hindsight",
	qdrant_reindex: "Przebudowa indeksu wyszukiwania",
	github_import: "Import otwartych issue GitHub",
	ai_catchup: "Uzupełnienie indeksu i pamięci AI",
};

const STATUS: Record<
	Run["status"],
	{ label: string; tone: "info" | "success" | "error" | "warning" }
> = {
	pending: { label: "w kolejce", tone: "info" },
	running: { label: "trwa", tone: "info" },
	done: { label: "zakończone", tone: "success" },
	failed: { label: "błąd", tone: "error" },
	cancelled: { label: "anulowane", tone: "warning" },
};

const isActive = (r: Run) => r.status === "pending" || r.status === "running";
/** Przerwane wracają od miejsca przerwania (backend `runs::RESUMABLE`). */
const RESUMABLE: RunKind[] = ["hindsight_seed", "qdrant_reindex", "github_import"];
/** Parametry wznowienia — w historii jako „wznowione po restarcie N×”, nie surowy JSON. */
const INTERNAL_PARAMS = ["base", "resumes"];

function ProgressView({ progress }: { progress: Record<string, unknown> }) {
	const done = Number(progress.done);
	const total = Number(progress.total);
	const entries = Object.entries(progress).filter(([k]) => k !== "done" && k !== "total");
	return (
		<div className="space-y-1">
			{Number.isFinite(done) && total > 0 ? (
				<div className="flex items-center gap-2">
					<div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
						<div
							className="h-full bg-primary"
							style={{ width: `${Math.min(100, (done / total) * 100)}%` }}
						/>
					</div>
					<span className="tabular-nums">
						{done.toLocaleString("pl-PL")} / {total.toLocaleString("pl-PL")}
					</span>
				</div>
			) : null}
			{entries.length ? (
				<p className="text-muted-foreground">
					{entries
						.map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : String(v)}`)
						.join(" · ")}
				</p>
			) : null}
		</div>
	);
}

function RunRow({ run }: { run: Run }) {
	const [showReport, setShowReport] = useState(false);
	const cancel = useHelpdeskMutation(() => helpdeskApi.cancelRun(run.id), { success: "Anulowano" });
	const resume = useHelpdeskMutation(() => helpdeskApi.resumeRun(run.id), {
		success: "Wznowiono od miejsca przerwania",
		error: "Nie udało się wznowić (to zadanie może już trwać)",
	});
	const st = STATUS[run.status];
	const params = Object.fromEntries(
		Object.entries(run.params ?? {}).filter(([k]) => !INTERNAL_PARAMS.includes(k)),
	);
	const resumes = Number(run.params?.resumes) || 0;
	return (
		<li
			className="space-y-1 rounded-md border border-border bg-card p-2 text-xs"
			data-run-id={run.id}
		>
			<div className="flex flex-wrap items-center gap-2">
				<span className="font-medium">{KIND_LABEL[run.kind] ?? run.kind}</span>
				<span className={cn(badgeBase, statusClasses[st.tone])}>{st.label}</span>
				{run.auto ? (
					<span className={cn(badgeBase, "border-border bg-muted text-muted-foreground")}>
						automatyczne
					</span>
				) : null}
				{run.status === "running" ? (
					<Loader2 className="size-3.5 animate-spin text-primary" />
				) : null}
				<span className="text-muted-foreground">
					{formatDateTime(run.createdAt)}
					{run.finishedAt ? ` → ${formatDateTime(run.finishedAt)}` : ""}
				</span>
				{resumes ? (
					<span className="text-muted-foreground">wznowione po restarcie {resumes}×</span>
				) : null}
				{Object.keys(params).length ? (
					<span className="text-muted-foreground">{JSON.stringify(params)}</span>
				) : null}
				{run.status === "failed" && RESUMABLE.includes(run.kind) ? (
					<Button
						size="sm"
						variant="outline"
						className="ml-auto h-6 text-xs"
						disabled={resume.isPending}
						onClick={() => resume.mutate(undefined)}
					>
						Wznów
					</Button>
				) : null}
				{isActive(run) ? (
					<Button
						size="sm"
						variant="outline"
						className="ml-auto h-6 text-xs"
						disabled={cancel.isPending}
						onClick={() => cancel.mutate(undefined)}
					>
						Anuluj
					</Button>
				) : null}
			</div>
			{isActive(run) || Object.keys(run.progress ?? {}).length ? (
				<ProgressView progress={run.progress ?? {}} />
			) : null}
			{run.error ? <p className="text-status-error-text">{run.error}</p> : null}
			{run.report != null ? (
				<div>
					<button
						type="button"
						className="inline-flex items-center gap-1 text-primary hover:underline"
						onClick={() => setShowReport((v) => !v)}
					>
						<ChevronDown
							className={cn("size-3.5 transition-transform", showReport && "rotate-180")}
						/>
						{showReport ? "Ukryj raport" : "Pokaż raport"}
					</button>
					{showReport ? (
						<pre className="mt-1 max-h-80 overflow-auto rounded-md bg-muted p-2 font-mono text-[11px]">
							{JSON.stringify(run.report, null, 2)}
						</pre>
					) : null}
				</div>
			) : null}
		</li>
	);
}

/** Import uzupełniający: tylko zgłoszenia zmienione od daty / od numeru OTRS. */
function IncrementalDialog({
	open,
	onOpenChange,
	onStart,
	pending,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onStart: (params: Record<string, unknown>) => void;
	pending: boolean;
}) {
	const [changedSince, setChangedSince] = useState("");
	const [minTicketId, setMinTicketId] = useState("");
	const valid = !!changedSince || /^\d+$/.test(minTicketId);
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<DialogTitle>Import uzupełniający z OTRS</DialogTitle>
					<DialogDescription>
						Dociąga zgłoszenia zmienione po pierwszym imporcie. Zaimportowane wcześniej nie dublują
						się.
					</DialogDescription>
				</DialogHeader>
				<DialogBody className="space-y-3">
					<div className="space-y-1">
						<Label htmlFor="hd-changed-since" className="text-xs">
							Zmienione od dnia
						</Label>
						<Input
							id="hd-changed-since"
							type="date"
							value={changedSince}
							onChange={(e) => setChangedSince(e.target.value)}
						/>
					</div>
					<div className="space-y-1">
						<Label htmlFor="hd-min-ticket" className="text-xs">
							albo od ID zgłoszenia OTRS
						</Label>
						<Input
							id="hd-min-ticket"
							inputMode="numeric"
							value={minTicketId}
							onChange={(e) => setMinTicketId(e.target.value.trim())}
						/>
					</div>
				</DialogBody>
				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						Anuluj
					</Button>
					<Button
						disabled={!valid || pending}
						onClick={() =>
							onStart({
								incremental: true,
								...(changedSince ? { changedSince } : {}),
								...(minTicketId ? { minTicketId: Number(minTicketId) } : {}),
							})
						}
					>
						Uruchom
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}

export function RunsPanel() {
	const key = useHelpdeskKey();
	const { data: runs = [] } = useQuery({
		queryKey: key("runs"),
		queryFn: helpdeskApi.runs,
		refetchInterval: (q) => (q.state.data?.some(isActive) ? 3000 : 30_000),
	});
	const start = useHelpdeskMutation(
		({ kind, params }: { kind: RunKind; params?: Record<string, unknown> }) =>
			helpdeskApi.startRun(kind, params),
		{
			success: (run) => `Uruchomiono: ${KIND_LABEL[run.kind] ?? run.kind}`,
			error: "Nie udało się uruchomić (to zadanie może już trwać)",
		},
	);
	const [confirmImport, setConfirmImport] = useState(false);
	const [incrementalOpen, setIncrementalOpen] = useState(false);
	const running = (kind: RunKind) => runs.some((r) => r.kind === kind && isActive(r));
	const otrsBusy = running("otrs_dry_run") || running("otrs_import");

	const card = (
		title: string,
		description: string,
		Icon: typeof Database,
		children: React.ReactNode,
	) => (
		<div className="space-y-2 rounded-lg border border-border bg-card p-3">
			<div className="flex items-center gap-2">
				<Icon className="size-4 text-muted-foreground" />
				<h3 className="text-sm font-semibold">{title}</h3>
			</div>
			<p className="text-xs text-muted-foreground">{description}</p>
			<div className="flex flex-wrap gap-2">{children}</div>
		</div>
	);

	return (
		<div className="space-y-4">
			<div className="grid gap-3 md:grid-cols-2">
				{card(
					"Import z OTRS",
					"Najpierw „Próba” — liczy zgłoszenia, adresy i załączniki bez zapisu i pokazuje raport. Potem import właściwy; uzupełniający dociąga zmiany po nim.",
					Upload,
					<>
						<Button
							size="sm"
							variant="outline"
							disabled={otrsBusy || start.isPending}
							onClick={() => start.mutate({ kind: "otrs_dry_run" })}
						>
							Próba (raport)
						</Button>
						<Button
							size="sm"
							disabled={otrsBusy || start.isPending}
							onClick={() => setConfirmImport(true)}
						>
							Importuj
						</Button>
						<Button
							size="sm"
							variant="outline"
							disabled={otrsBusy || start.isPending}
							onClick={() => setIncrementalOpen(true)}
						>
							Import uzupełniający
						</Button>
					</>,
				)}
				{card(
					"Zasil Hindsight",
					"Przekazuje zakończone zgłoszenia (wersja z ukrytymi danymi wrażliwymi) do pamięci AI.",
					Database,
					<Button
						size="sm"
						variant="outline"
						disabled={running("hindsight_seed") || start.isPending}
						onClick={() => start.mutate({ kind: "hindsight_seed" })}
					>
						Zasil Hindsight
					</Button>,
				)}
				{card(
					"Indeks wyszukiwania",
					"Buduje od nowa indeks semantyczny wyszukiwarki dawnych zgłoszeń.",
					Search,
					<Button
						size="sm"
						variant="outline"
						disabled={running("qdrant_reindex") || start.isPending}
						onClick={() => start.mutate({ kind: "qdrant_reindex" })}
					>
						Przebuduj indeks wyszukiwania
					</Button>,
				)}
				{card(
					"Robaczek — GitHub",
					"Zakłada zgłoszenia Robaczka dla otwartych issue z GitHub, których jeszcze nie ma w helpdesku.",
					GitBranch,
					<Button
						size="sm"
						variant="outline"
						disabled={running("github_import") || start.isPending}
						onClick={() => start.mutate({ kind: "github_import" })}
					>
						Importuj otwarte issue GitHub
					</Button>,
				)}
			</div>

			<section className="space-y-2">
				<h3 className="text-sm font-semibold">Historia zadań</h3>
				{runs.length ? (
					<ul className="space-y-1.5">
						{runs.map((r) => (
							<RunRow key={r.id} run={r} />
						))}
					</ul>
				) : (
					<p className="text-xs text-muted-foreground">Jeszcze nic nie uruchomiono.</p>
				)}
			</section>

			<ConfirmDialog
				open={confirmImport}
				onOpenChange={setConfirmImport}
				title="Uruchomić import z OTRS?"
				description="Zapisze historię zgłoszeń z OTRS (dane wrażliwe ukryte). Import obciąża serwer OTRS — idzie partiami z pauzą z konfiguracji."
				variant="warning"
				confirmLabel="Importuj"
				isLoading={start.isPending}
				onConfirm={() =>
					start.mutate({ kind: "otrs_import" }, { onSuccess: () => setConfirmImport(false) })
				}
			/>
			<IncrementalDialog
				open={incrementalOpen}
				onOpenChange={setIncrementalOpen}
				pending={start.isPending}
				onStart={(params) =>
					start.mutate(
						{ kind: "otrs_import", params },
						{ onSuccess: () => setIncrementalOpen(false) },
					)
				}
			/>
		</div>
	);
}
